/**
 * EditorMaker - CodeMirror 6 Editor Factory
 * 
 * Creates syntax-highlighted code editors with a scholarly aesthetic.
 * Supports JavaScript, HTML, CSS, Markdown, and LaTeX.
 * 
 * Usage:
 *   // Create editors with the unified API
 *   const editor = EditorMaker.createEditor("#container", "javascript");
 *   const editor = EditorMaker.createEditor("#container", "latex", { 
 *     theme: "scholarly",
 *     lineWrapping: true 
 *   });
 *   
 *   // Or use the convenience functions
 *   const jsEditor = EditorMaker.createJSEditor("#container");
 *   const htmlEditor = EditorMaker.createHTMLEditor("#container", "solarizedLight");
 *   const latexEditor = EditorMaker.createLaTeXEditor("#container");
 *   
 *   // Get/set content
 *   const code = editor.getContent();
 *   editor.setContent("new code here");
 * 
 * Available themes:
 *   - "scholarly" (default) - Custom theme matching BTT Mobile design tokens
 *   - Thememirror themes: amy, ayuLight, barf, bespin, birdsOfParadise, 
 *     boysAndGirls, clouds, cobalt, coolGlow, dracula, espresso, 
 *     noctisLilac, rosePineDawn, smoothy, solarizedLight, tomorrow
 *   - "default" - CodeMirror's default appearance
 */

import { basicSetup, EditorView } from "codemirror";
import { keymap } from "@codemirror/view";
import { EditorState } from "@codemirror/state";
import { javascript } from "@codemirror/lang-javascript";
import { html } from "@codemirror/lang-html";
import { css } from "@codemirror/lang-css";
import { markdown } from "@codemirror/lang-markdown";
import { latex } from "codemirror-lang-latex";
import { indentWithTab } from "@codemirror/commands";
import { indentUnit } from "@codemirror/language";
import { placeholder as cmPlaceholder } from "@codemirror/view";
import { HighlightStyle, syntaxHighlighting } from "@codemirror/language";
import { tags } from "@lezer/highlight";
import * as Themes from "thememirror";

// =============================================================================
// Scholarly Theme - matches BTT Mobile design tokens
// =============================================================================

// Design tokens from the BTT Mobile scholarly system
const scholarlyColors = {
  // Ink colors (text)
  inkDark: "#2c2c2c",
  inkMedium: "#555555",
  inkLight: "#888888",
  inkFaint: "#aaaaaa",
  
  // Paper colors (backgrounds)
  paperWhite: "#ffffff",
  paperCream: "#faf9f7",
  paperWarm: "#f5f4f2",
  paperMuted: "#e8e5e0",
  paperAged: "#d4d0c8",
  
  // Accent colors
  accentPrimary: "#8b7355",  // warm brown
  burgundy: "#8b4a4a",
  navy: "#4a5a6b",
  green: "#4a6b5a",
  gold: "#b8a060",
  
  // Syntax-specific (derived from accent palette)
  keyword: "#8b5a2b",        // warm brown, bolder
  string: "#2e7d32",         // green for strings
  comment: "#78909c",        // muted blue-gray
  function: "#5d4037",       // dark brown
  number: "#c62828",         // deep red
  property: "#4527a0",       // purple
  tag: "#6d5a43",            // muted brown
  attribute: "#1565c0",      // blue
  regexp: "#ad1457",         // magenta
};

// The scholarly editor theme (chrome: gutters, background, cursor, selection)
const scholarlyEditorTheme = EditorView.theme({
  "&": {
    color: scholarlyColors.inkDark,
    backgroundColor: scholarlyColors.paperCream,
    fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
    fontSize: "14px",
  },
  ".cm-content": {
    caretColor: scholarlyColors.accentPrimary,
    fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
  },
  ".cm-cursor, .cm-dropCursor": {
    borderLeftColor: scholarlyColors.accentPrimary,
    borderLeftWidth: "2px",
  },
  "&.cm-focused .cm-selectionBackground, .cm-selectionBackground, .cm-content ::selection": {
    backgroundColor: "rgba(139, 115, 85, 0.2)",
  },
  ".cm-panels": {
    backgroundColor: scholarlyColors.paperWarm,
    color: scholarlyColors.inkDark,
  },
  ".cm-panels.cm-panels-top": {
    borderBottom: `1px solid ${scholarlyColors.paperAged}`,
  },
  ".cm-panels.cm-panels-bottom": {
    borderTop: `1px solid ${scholarlyColors.paperAged}`,
  },
  ".cm-searchMatch": {
    backgroundColor: "rgba(184, 160, 96, 0.3)",
    outline: `1px solid ${scholarlyColors.gold}`,
  },
  ".cm-searchMatch.cm-searchMatch-selected": {
    backgroundColor: "rgba(139, 115, 85, 0.3)",
  },
  ".cm-activeLine": {
    backgroundColor: "rgba(139, 115, 85, 0.06)",
  },
  ".cm-selectionMatch": {
    backgroundColor: "rgba(139, 115, 85, 0.15)",
  },
  "&.cm-focused .cm-matchingBracket, &.cm-focused .cm-nonmatchingBracket": {
    outline: `1px solid ${scholarlyColors.accentPrimary}`,
    backgroundColor: "rgba(139, 115, 85, 0.2)",
  },
  ".cm-gutters": {
    backgroundColor: scholarlyColors.paperWarm,
    color: scholarlyColors.inkLight,
    border: "none",
    borderRight: `1px solid ${scholarlyColors.paperMuted}`,
  },
  ".cm-activeLineGutter": {
    backgroundColor: "rgba(139, 115, 85, 0.1)",
    color: scholarlyColors.inkMedium,
  },
  ".cm-foldPlaceholder": {
    backgroundColor: scholarlyColors.paperMuted,
    color: scholarlyColors.inkMedium,
    border: "none",
    padding: "0 4px",
    borderRadius: "2px",
  },
  ".cm-tooltip": {
    backgroundColor: scholarlyColors.paperWhite,
    border: `1px solid ${scholarlyColors.paperAged}`,
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  },
  ".cm-tooltip .cm-tooltip-arrow:before": {
    borderTopColor: scholarlyColors.paperAged,
    borderBottomColor: scholarlyColors.paperAged,
  },
  ".cm-tooltip .cm-tooltip-arrow:after": {
    borderTopColor: scholarlyColors.paperWhite,
    borderBottomColor: scholarlyColors.paperWhite,
  },
  ".cm-tooltip-autocomplete": {
    "& > ul > li[aria-selected]": {
      backgroundColor: "rgba(139, 115, 85, 0.15)",
      color: scholarlyColors.inkDark,
    },
  },
  // Placeholder styling
  ".cm-placeholder": {
    color: scholarlyColors.inkFaint,
    fontStyle: "italic",
  },
}, { dark: false });

// The scholarly syntax highlighting
const scholarlySyntaxHighlighting = HighlightStyle.define([
  // Keywords and control flow
  { tag: tags.keyword, color: scholarlyColors.keyword, fontWeight: "600" },
  { tag: tags.controlKeyword, color: scholarlyColors.keyword, fontWeight: "600" },
  { tag: tags.moduleKeyword, color: scholarlyColors.keyword, fontWeight: "600" },
  { tag: tags.operatorKeyword, color: scholarlyColors.keyword, fontWeight: "600" },
  
  // Functions and definitions
  { tag: tags.function(tags.variableName), color: scholarlyColors.function, fontWeight: "600" },
  { tag: tags.definition(tags.variableName), color: scholarlyColors.function },
  { tag: tags.definition(tags.propertyName), color: scholarlyColors.function },
  
  // Variables and properties
  { tag: tags.variableName, color: scholarlyColors.inkDark },
  { tag: tags.propertyName, color: scholarlyColors.property },
  { tag: tags.special(tags.variableName), color: scholarlyColors.burgundy },
  
  // Strings and literals
  { tag: tags.string, color: scholarlyColors.string },
  { tag: tags.special(tags.string), color: scholarlyColors.string },
  { tag: tags.regexp, color: scholarlyColors.regexp },
  { tag: tags.escape, color: scholarlyColors.regexp },
  
  // Numbers and booleans
  { tag: tags.number, color: scholarlyColors.number },
  { tag: tags.bool, color: scholarlyColors.number },
  { tag: tags.null, color: scholarlyColors.number },
  
  // Comments
  { tag: tags.comment, color: scholarlyColors.comment, fontStyle: "italic" },
  { tag: tags.lineComment, color: scholarlyColors.comment, fontStyle: "italic" },
  { tag: tags.blockComment, color: scholarlyColors.comment, fontStyle: "italic" },
  { tag: tags.docComment, color: scholarlyColors.comment, fontStyle: "italic" },
  
  // HTML/XML tags
  { tag: tags.tagName, color: scholarlyColors.tag, fontWeight: "600" },
  { tag: tags.attributeName, color: scholarlyColors.attribute },
  { tag: tags.attributeValue, color: scholarlyColors.string },
  { tag: tags.angleBracket, color: scholarlyColors.inkLight },
  
  // Types
  { tag: tags.typeName, color: scholarlyColors.attribute },
  { tag: tags.className, color: scholarlyColors.attribute },
  { tag: tags.namespace, color: scholarlyColors.attribute },
  
  // Operators and punctuation
  { tag: tags.operator, color: scholarlyColors.inkMedium },
  { tag: tags.punctuation, color: scholarlyColors.inkMedium },
  { tag: tags.bracket, color: scholarlyColors.inkMedium },
  { tag: tags.separator, color: scholarlyColors.inkMedium },
  
  // Markdown/document specific
  { tag: tags.heading, color: scholarlyColors.function, fontWeight: "bold" },
  { tag: tags.heading1, color: scholarlyColors.function, fontWeight: "bold", fontSize: "1.3em" },
  { tag: tags.heading2, color: scholarlyColors.function, fontWeight: "bold", fontSize: "1.2em" },
  { tag: tags.heading3, color: scholarlyColors.function, fontWeight: "bold", fontSize: "1.1em" },
  { tag: tags.quote, color: scholarlyColors.comment, fontStyle: "italic" },
  { tag: tags.link, color: scholarlyColors.attribute, textDecoration: "underline" },
  { tag: tags.url, color: scholarlyColors.attribute },
  { tag: tags.emphasis, fontStyle: "italic" },
  { tag: tags.strong, fontWeight: "bold" },
  { tag: tags.strikethrough, textDecoration: "line-through" },
  
  // LaTeX specific
  { tag: tags.meta, color: scholarlyColors.keyword },
  { tag: tags.processingInstruction, color: scholarlyColors.keyword },
  { tag: tags.labelName, color: scholarlyColors.attribute },
  
  // Invalid/error
  { tag: tags.invalid, color: "#ff0000", textDecoration: "underline wavy" },
]);

// Combined scholarly theme extension
const scholarlyTheme = [
  scholarlyEditorTheme,
  syntaxHighlighting(scholarlySyntaxHighlighting),
];

// =============================================================================
// Font Size Override - ensures consistent sizing across all themes
// =============================================================================

// This extension overrides font settings to maintain consistency
const fontSizeOverride = EditorView.theme({
  "&": {
    fontSize: "14px",
  },
  ".cm-content": {
    fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
  },
  ".cm-gutters": {
    fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
    fontSize: "14px",
  },
});

// =============================================================================
// Language Configuration
// =============================================================================

const LANGUAGES = {
  javascript: {
    extension: javascript,
    placeholder: "// JavaScript code...\n",
    options: { jsx: false, typescript: false }
  },
  jsx: {
    extension: javascript,
    placeholder: "// JSX code...\n",
    options: { jsx: true, typescript: false }
  },
  typescript: {
    extension: javascript,
    placeholder: "// TypeScript code...\n",
    options: { jsx: false, typescript: true }
  },
  tsx: {
    extension: javascript,
    placeholder: "// TSX code...\n",
    options: { jsx: true, typescript: true }
  },
  html: {
    extension: html,
    placeholder: "<!-- HTML markup -->\n",
    options: {}
  },
  css: {
    extension: css,
    placeholder: "/* CSS styles */\n",
    options: {}
  },
  markdown: {
    extension: markdown,
    placeholder: "# Markdown document\n\nStart writing...\n",
    options: {}
  },
  latex: {
    extension: latex,
    placeholder: "% LaTeX document\n\\documentclass{article}\n\\begin{document}\n\n\\end{document}\n",
    options: {
      autoCloseTags: true,
      enableLinting: true,
      enableTooltips: true
    }
  }
};

// =============================================================================
// Available Themes from thememirror
// =============================================================================

// List of actual theme names exported by thememirror
const AVAILABLE_THEMES = [
  'amy',
  'ayuLight', 
  'barf',
  'bespin',
  'birdsOfParadise',
  'boysAndGirls',
  'clouds',
  'cobalt',
  'coolGlow',
  'dracula',
  'espresso',
  'noctisLilac',
  'rosePineDawn',
  'smoothy',
  'solarizedLight',
  'tomorrow'
];

// =============================================================================
// Editor Factory
// =============================================================================

/**
 * Create a CodeMirror editor instance
 * 
 * @param {string|Element} selector - CSS selector or DOM element for the container
 * @param {string} language - Language mode: "javascript", "html", "css", "markdown", "latex", etc.
 * @param {Object} options - Configuration options
 * @param {string} options.theme - Theme name: "scholarly" (default), "default", or any thememirror theme
 * @param {string} options.doc - Initial document content (overrides placeholder)
 * @param {string} options.placeholder - Placeholder text when editor is empty
 * @param {number} options.tabSize - Spaces per tab (default: 4)
 * @param {boolean} options.lineWrapping - Enable line wrapping (default: true)
 * @returns {EditorView} The editor instance with added convenience methods
 */
function createEditor(selector, language, options = {}) {
  const {
    theme = "scholarly",
    doc = null,
    placeholder = null,
    tabSize = 4,
    lineWrapping = true,
  } = options;

  // Get language configuration
  const lang = LANGUAGES[language];
  if (!lang) {
    const supported = Object.keys(LANGUAGES).join(", ");
    throw new Error(`Unsupported language: "${language}". Supported: ${supported}`);
  }

  // Resolve container element
  const container = typeof selector === "string" 
    ? document.querySelector(selector) 
    : selector;
  
  if (!container) {
    throw new Error(`Container not found: ${selector}`);
  }

  // Build extensions array
  const extensions = [
    basicSetup,
    keymap.of([indentWithTab]),
    indentUnit.of(" ".repeat(tabSize)),
  ];

  // Add theme
  if (theme === "scholarly") {
    extensions.push(scholarlyTheme);
  } else if (theme !== "default" && Themes[theme]) {
    extensions.push(Themes[theme]);
    // Add font size override to maintain consistent sizing
    extensions.push(fontSizeOverride);
  }
  // "default" uses CodeMirror's built-in styling

  // Add line wrapping
  if (lineWrapping) {
    extensions.push(EditorView.lineWrapping);
  }

  // Add placeholder
  const placeholderText = placeholder ?? lang.placeholder;
  if (placeholderText) {
    extensions.push(cmPlaceholder(placeholderText));
  }

  // Add language support
  extensions.push(lang.extension(lang.options));

  // Create editor
  const editor = new EditorView({
    parent: container,
    doc: doc ?? "",
    extensions,
  });

  // === Add convenience methods ===

  /**
   * Get the current editor content
   * @returns {string}
   */
  editor.getContent = function() {
    return this.state.doc.toString();
  };

  /**
   * Set the editor content
   * @param {string} content - New content
   */
  editor.setContent = function(content) {
    this.dispatch({
      changes: {
        from: 0,
        to: this.state.doc.length,
        insert: content
      }
    });
  };

  /**
   * Insert text at the current cursor position
   * @param {string} text - Text to insert
   */
  editor.insert = function(text) {
    const pos = this.state.selection.main.head;
    this.dispatch({
      changes: { from: pos, insert: text },
      selection: { anchor: pos + text.length }
    });
    this.focus();
  };

  /**
   * Get the current selection
   * @returns {string}
   */
  editor.getSelection = function() {
    return this.state.sliceDoc(
      this.state.selection.main.from,
      this.state.selection.main.to
    );
  };

  /**
   * Replace the current selection
   * @param {string} text - Replacement text
   */
  editor.replaceSelection = function(text) {
    this.dispatch(
      this.state.replaceSelection(text)
    );
  };

  /**
   * Focus the editor
   */
  editor.focusEditor = function() {
    this.focus();
  };

  // Legacy compatibility: toString returns content
  editor.toString = function() {
    return this.getContent();
  };

  return editor;
}

// =============================================================================
// Convenience Functions (backward compatible)
// =============================================================================

function createJSEditor(selector, theme = "scholarly") {
  return createEditor(selector, "javascript", { theme });
}

function createHTMLEditor(selector, theme = "scholarly") {
  return createEditor(selector, "html", { theme });
}

function createCSSEditor(selector, theme = "scholarly") {
  return createEditor(selector, "css", { theme });
}

function createMarkdownEditor(selector, theme = "scholarly") {
  return createEditor(selector, "markdown", { theme });
}

function createLaTeXEditor(selector, theme = "scholarly") {
  return createEditor(selector, "latex", { theme });
}

// =============================================================================
// Exports
// =============================================================================

export {
  // Main factory
  createEditor,
  
  // Convenience functions
  createJSEditor,
  createHTMLEditor,
  createCSSEditor,
  createMarkdownEditor,
  createLaTeXEditor,
  
  // Re-exports for advanced usage
  EditorView,
  EditorState,
  Themes,
  
  // Theme for use elsewhere
  scholarlyTheme,
  scholarlyColors,
  
  // Available theme names
  AVAILABLE_THEMES,
  
  // Language configurations (for inspection)
  LANGUAGES,
};
